/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable max-statements-per-line, no-else-return, consistent-return, sonarjs/no-identical-functions */
import { Box } from "@twilio-paste/core/box";
import { Text } from "@twilio-paste/core/text";
import { Button } from "@twilio-paste/core/button";
import { Spinner } from "@twilio-paste/core/spinner";
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import contactBackend from "../../utils/contactBackend";
import { recentChatsStorage, RecentChatItem } from "../../utils/recentChatsStorage";
import { openChatHistory, setRecentChatsExpanded } from "../../store/actions/genericActions";
import { AppState } from "../../store/definitions";
import { formatHistoryAuthorLabel } from "../../utils/formatHistoryAuthorLabel";
import { HistoryMessageBubble, HistoryFeedMessage } from "./HistoryMessageBubble";

type ViewMode = "list" | "messages";
type RangeOption = "day" | "month" | "year" | "";

const DATE_FMT: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
};

const toLocalInputValue = (date: Date) => {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const formatDateTime = (value?: string) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString("zh-CN", DATE_FMT);
};

const stateLabel = (state?: string) => {
    if (state === "active") return "进行中";
    if (state === "inactive") return "待处理";
    if (state === "closed") return "已结束";
    return state || "未知";
};

const SEGMENT_BG = "colorBackgroundBody";
const SEGMENT_ACTIVE_BG = "colorBackgroundPrimaryWeakest";
const SEGMENT_ACTIVE_TEXT = "colorText";
const SEGMENT_INACTIVE_TEXT = "colorTextWeak";
const FILTER_ACTIVE_BG = "colorBackgroundPrimaryWeakest";
const FILTER_ACTIVE_TEXT = "colorText";
const FILTER_INACTIVE_TEXT = "colorTextWeak";
const DATE_BORDER = "colorBorderWeak";
const FILTER_BG = "colorBackgroundBody";
const GROUP_BG = "colorBackgroundBody";
const GROUP_HOVER_BG = "colorBackgroundPrimaryWeakest";

// ---------- 日期分组工具 ----------
interface DayGroup {
    date: string; // "YYYY-MM-DD"
    chats: RecentChatItem[];
}
type MonthGroup =
    | { month: number; days: DayGroup[] }
    | { month: number; chats: RecentChatItem[] };
interface YearGroup {
    year: number;
    months?: MonthGroup[];
    chats?: RecentChatItem[];
}

const getDateField = (chat: RecentChatItem): string =>
    chat.dateUpdated || chat.dateCreated || "";

const parseDate = (value: string): Date => {
    if (!value) return new Date(0);
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? new Date(0) : d;
};

const dateKey = (value: string) => value ? value.slice(0, 10) : "";

const buildGroupByDay = (chats: RecentChatItem[]): YearGroup[] => {
    const map = new Map<number, Map<number, Map<string, RecentChatItem[]>>>();
    chats.forEach((chat) => {
        const d = parseDate(getDateField(chat));
        const y = d.getFullYear();
        const m = d.getMonth();
        if (!map.has(y)) map.set(y, new Map<number, Map<string, RecentChatItem[]>>());
        const monthMap = map.get(y)!;
        if (!monthMap.has(m)) monthMap.set(m, new Map<string, RecentChatItem[]>());
        const dayMap = monthMap.get(m)!;
        const dk = dateKey(getDateField(chat));
        if (!map.has(y)) map.set(y, new Map<number, Map<string, RecentChatItem[]>>());
        const mm = map.get(y)!;
        if (!mm.has(m)) mm.set(m, new Map<string, RecentChatItem[]>());
        const dm = mm.get(m)!;
        if (!dm.has(dk)) dm.set(dk, []);
        dm.get(dk)!.push(chat);
    });
    return Array.from(map.entries())
        .sort(([a], [b]) => b - a)
        .map(([year, monthMap]) => ({
            year,
            months: Array.from(monthMap.entries())
                .sort(([a], [b]) => b - a)
                .map(([month, dayMap]) => ({
                    month,
                    days: Array.from(dayMap.entries())
                        .sort(([a], [b]) => b.localeCompare(a))
                        .map(([date, dayChats]) => ({ date, chats: dayChats }))
                }))
        }));
};

const buildGroupByMonth = (chats: RecentChatItem[]): YearGroup[] => {
    const map = new Map<number, Map<number, RecentChatItem[]>>();
    chats.forEach((chat) => {
        const d = parseDate(getDateField(chat));
        const y = d.getFullYear();
        const m = d.getMonth();
        if (!map.has(y)) map.set(y, new Map<number, RecentChatItem[]>());
        const monthMap = map.get(y)!;
        if (!monthMap.has(m)) monthMap.set(m, []);
        monthMap.get(m)!.push(chat);
    });
    return Array.from(map.entries())
        .sort(([a], [b]) => b - a)
        .map(([year, monthMap]) => ({
            year,
            months: Array.from(monthMap.entries())
                .sort(([a], [b]) => b - a)
                .map(([month, monthChats]) => ({ month, chats: monthChats }))
        }));
};

const buildGroupByYear = (chats: RecentChatItem[]): YearGroup[] => {
    const map = new Map<number, RecentChatItem[]>();
    chats.forEach((chat) => {
        const d = parseDate(getDateField(chat));
        const y = d.getFullYear();
        if (!map.has(y)) map.set(y, []);
        map.get(y)!.push(chat);
    });
    return Array.from(map.entries())
        .sort(([a], [b]) => b - a)
        .map(([year, yearChats]) => ({ year, chats: yearChats }));
};

const MONTH_NAMES = ["1月","2月","3月","4月","5月","6月","7月","8月","9月","10月","11月","12月"];
const DAY_MONTH_NAMES = ["1日","2日","3日","4日","5日","6日","7日","8日","9日","10日","11日","12日","13日","14日","15日","16日","17日","18日","19日","20日","21日","22日","23日","24日","25日","26日","27日","28日","29日","30日","31日"];

type MsgDayGroup = { date: string; chats: HistoryFeedMessage[] };
type MsgMonthGroup =
    | { month: number; days: MsgDayGroup[] }
    | { month: number; chats: HistoryFeedMessage[] };
type MsgYearGroup = { year: number; months?: MsgMonthGroup[]; chats?: HistoryFeedMessage[] };

const buildMsgGroupByDay = (msgs: HistoryFeedMessage[]): MsgYearGroup[] => {
    const map = new Map<number, Map<number, Map<string, HistoryFeedMessage[]>>>();
    msgs.forEach((msg) => {
        const d = parseDate(msg.dateCreated ?? "");
        const y = d.getFullYear();
        const m = d.getMonth();
        if (!map.has(y)) map.set(y, new Map<number, Map<string, HistoryFeedMessage[]>>());
        const monthMap = map.get(y)!;
        if (!monthMap.has(m)) monthMap.set(m, new Map<string, HistoryFeedMessage[]>());
        const dayMap = monthMap.get(m)!;
        const dk = dateKey(msg.dateCreated ?? "");
        if (!dayMap.has(dk)) dayMap.set(dk, []);
        dayMap.get(dk)!.push(msg);
    });
    return Array.from(map.entries())
        .sort(([a], [b]) => b - a)
        .map(([year, monthMap]) => ({
            year,
            months: Array.from(monthMap.entries())
                .sort(([a], [b]) => b - a)
                .map(([month, dayMap]) => ({
                    month,
                    days: Array.from(dayMap.entries())
                        .sort(([a], [b]) => b.localeCompare(a))
                        .map(([date, dayMsgs]) => ({ date, chats: dayMsgs }))
                }))
        }));
};

const buildMsgGroupByMonth = (msgs: HistoryFeedMessage[]): MsgYearGroup[] => {
    const map = new Map<number, Map<number, HistoryFeedMessage[]>>();
    msgs.forEach((msg) => {
        const d = parseDate(msg.dateCreated ?? "");
        const y = d.getFullYear();
        const m = d.getMonth();
        if (!map.has(y)) map.set(y, new Map<number, HistoryFeedMessage[]>());
        const monthMap = map.get(y)!;
        if (!monthMap.has(m)) monthMap.set(m, []);
        monthMap.get(m)!.push(msg);
    });
    return Array.from(map.entries())
        .sort(([a], [b]) => b - a)
        .map(([year, monthMap]) => ({
            year,
            months: Array.from(monthMap.entries())
                .sort(([a], [b]) => b - a)
                .map(([month, monthMsgs]) => ({ month, chats: monthMsgs }))
        }));
};

const buildMsgGroupByYear = (msgs: HistoryFeedMessage[]): MsgYearGroup[] => {
    const map = new Map<number, HistoryFeedMessage[]>();
    msgs.forEach((msg) => {
        const d = parseDate(msg.dateCreated ?? "");
        const y = d.getFullYear();
        if (!map.has(y)) map.set(y, []);
        map.get(y)!.push(msg);
    });
    return Array.from(map.entries())
        .sort(([a], [b]) => b - a)
        .map(([year, yearMsgs]) => ({ year, chats: yearMsgs }));
};

const ChatCard = ({ chat, onOpen }: { chat: RecentChatItem; onOpen: () => void }) => (
    <Box
        as="button"
        type="button"
        textAlign="left"
        padding="space40"
        backgroundColor="colorBackgroundBody"
        borderRadius="borderRadius20"
        borderStyle="solid"
        borderWidth="borderWidth10"
        borderColor="colorBorderWeak"
        cursor="pointer"
        display="block"
        width="100%"
        onClick={onOpen}
        _hover={{ borderColor: "colorBorderPrimary", backgroundColor: GROUP_HOVER_BG }}
        transition="border-color 150ms, background-color 150ms"
    >
        <Box display="flex" justifyContent="space-between" alignItems="center">
            <Text as="span" fontWeight="fontWeightSemibold" color="colorText" fontSize="fontSize30">
                {chat.serviceType || "在线咨询"}
            </Text>
            <Text as="span" fontSize="fontSize20" color="colorTextWeak">
                {stateLabel(chat.state)}
            </Text>
        </Box>
        <Text as="p" fontSize="fontSize20" color="colorTextWeak" marginTop="space20">
            {formatDateTime(getDateField(chat))}
        </Text>
        <Text as="p" marginTop="space20" color="colorText" fontSize="fontSize20">
            {chat.preview || "暂无消息预览"}
        </Text>
    </Box>
);

// 单条消息卡片
const MsgCard = ({ msg, customerEmail }: { msg: HistoryFeedMessage; customerEmail?: string }) => (
    <Box
        marginBottom="space20"
        padding="space30"
        backgroundColor="colorBackgroundBody"
        borderRadius="borderRadius20"
        borderStyle="solid"
        borderWidth="borderWidth10"
        borderColor="colorBorderWeak"
    >
        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="space20">
            <Text as="span" fontSize="fontSize20" color="colorTextWeak">
                {formatDateTime(msg.dateCreated)}
            </Text>
            <Text as="span" fontSize="fontSize20" fontWeight="fontWeightSemibold" color="colorText">
                {formatHistoryAuthorLabel(msg.author, msg.conversationSid, {
                    customerEmail,
                    agentFriendlyName: msg.agentFriendlyName
                })}
            </Text>
        </Box>
        <Text as="p" fontSize="fontSize20" color="colorText" whiteSpace="pre-wrap" wordBreak="break-word">
            {msg.body || ""}
        </Text>
    </Box>
);

// 折叠组头
const GroupHeader = ({
    label,
    count,
    open,
    onToggle
}: {
    label: string;
    count: number;
    open: boolean;
    onToggle: () => void;
}) => (
    <Box
        as="button"
        type="button"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        background="none"
        border="none"
        cursor="pointer"
        paddingY="space30"
        paddingX="space30"
        width="100%"
        onClick={onToggle}
        borderRadius="borderRadius10"
        transition="all 200ms"
        position="relative"
        _hover={{
            backgroundColor: "colorBackgroundPrimaryWeakest",
        }}
    >
        {/* 左侧：展开箭头 + 标签 */}
        <Box display="flex" alignItems="center" columnGap="space30">
            {/* 动态箭头 */}
            <Box
                as="span"
                width="16px"
                height="16px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                transition="transform 250ms cubic-bezier(0.4, 0, 0.2, 1)"
                style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)" }}
            >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                        d="M6 4L10 8L6 12"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </Box>

            {/* 标签文字 */}
            <Text
                as="span"
                fontWeight="fontWeightMedium"
                color="colorText"
                fontSize="fontSize30"
                letterSpacing="0.01em"
            >
                {label}
            </Text>
        </Box>

        {/* 右侧：数量徽章 */}
        <Box
            as="span"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            paddingY="space10"
            paddingX="space20"
            borderRadius="borderRadiusCircle"
            backgroundColor="colorBackgroundPrimaryWeakest"
            borderWidth="borderWidth10"
            borderStyle="solid"
            borderColor="colorBorderPrimary"
            minWidth="40px"
            transition="all 200ms"
        >
            <Text
                as="span"
                fontSize="fontSize20"
                fontWeight="fontWeightBold"
                color="colorText"
            >
                {count}
            </Text>
        </Box>
    </Box>
);

// ---------- 主组件 ----------
export const RecentChatsPanel = ({ email }: { email?: string }) => {
    const dispatch = useDispatch<any>();
    const expanded = useSelector((state: AppState) => state.session.recentChatsExpanded);

    const [range, setRange] = useState<RangeOption>("day");
    const [viewMode, setViewMode] = useState<ViewMode>("list");
    const [customStart, setCustomStart] = useState("");
    const [customEnd, setCustomEnd] = useState("");
    const [showCustom, setShowCustom] = useState(false);

    const [chats, setChats] = useState<RecentChatItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const setExpanded = (next: boolean) => {
        dispatch(setRecentChatsExpanded(next));
    };

    // 按消息模式：全量缓存 + 本地过滤
    const [allMessages, setAllMessages] = useState<HistoryFeedMessage[]>([]);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [messagesLoaded, setMessagesLoaded] = useState(false); // 是否已拉取过

    const scrollRef = useRef<HTMLDivElement>(null);
    const loadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // 展开/折叠状态
    const [yearOpen, setYearOpen] = useState<Record<number, boolean>>({});
    const [monthOpen, setMonthOpen] = useState<Record<string, boolean>>({}); // key: "YYYY-MM"
    const [dayOpen, setDayOpen] = useState<Record<string, boolean>>({}); // key: "YYYY-MM-DD"

    const toggleYear = (y: number) => setYearOpen((p) => ({ ...p, [y]: !p[y] }));
    const toggleMonth = (key: string) => setMonthOpen((p) => ({ ...p, [key]: !p[key] }));
    const toggleDay = (key: string) => setDayOpen((p) => ({ ...p, [key]: !p[key] }));

    const filteredChats = useMemo(() => {
        if (!(showCustom && customStart && customEnd)) {
            return chats;
        }
        const start = new Date(customStart);
        start.setHours(0, 0, 0, 0);
        const end = new Date(customEnd);
        end.setHours(23, 59, 59, 999);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
            return chats;
        }
        return chats.filter((chat) => {
            const d = new Date(chat.dateUpdated || chat.dateCreated || "");
            if (Number.isNaN(d.getTime())) {
                return false;
            }
            return d >= start && d <= end;
        });
    }, [chats, showCustom, customStart, customEnd]);

    // 分组数据
    const yearGroups = (() => {
        if (range === "day") return buildGroupByDay(filteredChats);
        if (range === "month") return buildGroupByMonth(filteredChats);
        if (range === "year") return buildGroupByYear(filteredChats);
        return null;
    })();

    useEffect(() => {
        setRange("day");
        setViewMode("list");
        setShowCustom(false);
        setChats([]);
        setAllMessages([]);
        setMessagesLoaded(false);
        setYearOpen({});
        setMonthOpen({});
        setDayOpen({});
    }, [email]);

    // ---------- 本地消息过滤 ----------
    // eslint-disable-next-line max-statements-per-line, consistent-return
    const filteredMessages = useMemo(() => {
        if (!showCustom && !range) return allMessages;
        const msgs = allMessages;
        // 按天/按月/按年：返回全量，分组渲染会处理
        if (range === "day" || range === "month" || range === "year") return msgs;
        // 自定义区间才做时间过滤
        if (showCustom && customStart && customEnd) {
            const s = new Date(customStart);
            s.setHours(0, 0, 0, 0);
            const e = new Date(customEnd);
            e.setHours(23, 59, 59, 999);
            if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return msgs;
            return msgs.filter((m) => {
                const d = new Date(m.dateCreated ?? "");
                if (Number.isNaN(d.getTime())) return false;
                return d >= s && d <= e;
            });
        }
        return msgs;
    }, [allMessages, range, showCustom, customStart, customEnd]);

    const loadChats = useCallback(async () => {
        const normalized = (email || "").trim().toLowerCase();
        if (!normalized || !normalized.includes("@")) return;
        setError("");
        const cached = recentChatsStorage.list(normalized);
        if (cached.length > 0) {
            setChats(cached);
            setLoading(false);
        } else {
            setLoading(true);
        }
        try {
            const body: Record<string, unknown> = { email };
            if (showCustom && customStart && customEnd) {
                body.range = "custom";
                body.startDate = customStart;
                body.endDate = customEnd;
            }
            const resp = await contactBackend<{ chats?: RecentChatItem[] }>(
                "/recentChats",
                body
            );
            const merged = recentChatsStorage.merge(normalized, resp?.chats || []);
            setChats(merged);
        } catch {
            if (cached.length === 0) {
                setError("加载失败");
            }
        } finally {
            setLoading(false);
        }
    }, [email, showCustom, customStart, customEnd]);

    const loadAllMessages = useCallback(async () => {
        const normalized = (email || "").trim().toLowerCase();
        if (!normalized || !normalized.includes("@")) {
            return;
        }
        setMessagesLoading(true);
        setError("");
        try {
            const resp = await contactBackend<{ messages?: HistoryFeedMessage[] }>(
                "/historyFeed/all",
                { email }
            );
            setAllMessages(resp.messages || []);
            setMessagesLoaded(true);
        } catch {
            setError("加载历史消息失败");
        } finally {
            setMessagesLoading(false);
        }
    }, [email]);

    const scheduleReload = useCallback(() => {
        if (loadTimerRef.current) clearTimeout(loadTimerRef.current);
        // 延后到下一拍，避免挡住主页首屏渲染
        loadTimerRef.current = setTimeout(() => {
            if (viewMode === "list") loadChats();
            else if (viewMode === "messages" && !messagesLoaded) loadAllMessages();
        }, 0);
    }, [viewMode, messagesLoaded, loadChats, loadAllMessages]);

    // 仅展开时再拉远端；折叠时主页可立刻操作表单
    useEffect(() => {
        if (!email?.trim() || !expanded) return undefined;
        scheduleReload();
        return () => {
            const t = loadTimerRef.current;
            if (t) clearTimeout(t);
        };
    }, [email, expanded, scheduleReload]);

    // 有邮箱时先用本地缓存点亮列表（不阻塞），远端等展开再刷新
    useEffect(() => {
        const normalized = (email || "").trim().toLowerCase();
        if (!normalized || !normalized.includes("@")) {
            return;
        }
        const cached = recentChatsStorage.list(normalized);
        if (cached.length > 0) {
            setChats(cached);
        }
    }, [email]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, [viewMode]);

    const selectRange = (r: RangeOption) => {
        setRange(r);
        setShowCustom(false);
        setCustomStart("");
        setCustomEnd("");
        setYearOpen({});
        setMonthOpen({});
        setDayOpen({});
    };

    const activateCustom = () => {
        setRange("");
        setShowCustom(true);
        if (!customEnd) {
            const today = new Date();
            setCustomEnd(toLocalInputValue(today));
            const past = new Date(today);
            past.setMonth(past.getMonth() - 1);
            setCustomStart(toLocalInputValue(past));
        }
    };

    if (!email?.trim() || !email.includes("@")) {
        return null;
    }

    // ---- 分段控件（视图切换） ----
    const SegmentedControl = ({ value, onChange, options }: {
        value: string;
        onChange: (v: string) => void;
        options: { label: string; value: string }[];
    }) => (
        <Box
            display="inline-flex"
            backgroundColor={SEGMENT_BG}
            borderRadius="borderRadius20"
            padding="space10"
            columnGap="space10"
        >
            {options.map((opt) => {
                const active = value === opt.value;
                return (
                    <Box
                        key={opt.value}
                        as="button"
                        type="button"
                        onClick={() => onChange(opt.value)}
                        paddingY="space10"
                        paddingX="space50"
                        borderRadius="borderRadius20"
                        backgroundColor={active ? SEGMENT_ACTIVE_BG : "transparent"}
                        cursor="pointer"
                        transition="background-color 150ms"
                        _hover={{ backgroundColor: active ? SEGMENT_ACTIVE_BG : "colorBackgroundPrimaryWeakest" }}
                    >
                        <Text
                            as="span"
                            fontSize="fontSize20"
                            fontWeight={active ? "fontWeightSemibold" : "fontWeightNormal"}
                            color={active ? SEGMENT_ACTIVE_TEXT : SEGMENT_INACTIVE_TEXT}
                            whiteSpace="nowrap"
                        >
                            {opt.label}
                        </Text>
                    </Box>
                );
            })}
        </Box>
    );

    // ---- 紧凑筛选胶囊 ----
    const FilterChip = ({
        label,
        active,
        onClick
    }: {
        label: string;
        active: boolean;
        onClick: () => void;
    }) => (
        <Box
            as="button"
            type="button"
            onClick={onClick}
            paddingY="space10"
            paddingX="space30"
            borderRadius="borderRadius20"
            borderStyle="solid"
            borderWidth="borderWidth10"
            borderColor={active ? "colorBorderPrimary" : "colorBorderWeak"}
            backgroundColor={active ? FILTER_ACTIVE_BG : "transparent"}
            cursor="pointer"
            transition="all 150ms"
            _hover={{ borderColor: "colorBorderPrimary" }}
        >
            <Text
                as="span"
                fontSize="fontSize20"
                color={active ? FILTER_ACTIVE_TEXT : FILTER_INACTIVE_TEXT}
                fontWeight={active ? "fontWeightSemibold" : "fontWeightNormal"}
                whiteSpace="nowrap"
            >
                {label}
            </Text>
        </Box>
    );

    // ---- 渲染分组列表 ----
    const renderGroupedChats = () => {
        if (!yearGroups) return null;
        if (yearGroups.length === 0) {
            return <Text as="p" color="colorTextWeak">暂无历史会话。</Text>;
        }

        return (
            <Box maxHeight="400px" overflowY="auto">
                {yearGroups.map((yg) => {
                    const y = yg.year;
                    const isYOpen = yearOpen[y] === true; // 默认展开
                    return (
                        <Box key={y} marginBottom="space20">
                            {/* 年份头 */}
                            <GroupHeader
                                label={`${y} 年`}
                                count={yg.months
                                    ? yg.months.reduce(
                                          (s, mg) =>
                                              s +
                                              ("chats" in mg
                                                  ? mg.chats.length
                                                  : mg.days.reduce(
                                                        (ds: number, d: DayGroup) => ds + d.chats.length,
                                                        0
                                                    )),
                                          0
                                      )
                                    : (yg.chats?.length ?? 0)}
                                open={isYOpen}
                                onToggle={() => toggleYear(y)}
                            />
                            {isYOpen && (
                                <Box paddingLeft="space40">
                                    {yg.months ? (
                                        yg.months.map((mg) => {
                                        if ("chats" in mg) {
                                            // 按月分组
                                            const isMOpen = monthOpen[`${y}-${mg.month}`] === true;
                                            return (
                                                <Box key={mg.month} marginBottom="space10">
                                                    <GroupHeader
                                                        label={MONTH_NAMES[mg.month]}
                                                        count={mg.chats.length}
                                                        open={isMOpen}
                                                        onToggle={() => toggleMonth(`${y}-${mg.month}`)}
                                                    />
                                                    {isMOpen && (
                                                        <Box display="flex" flexDirection="column" rowGap="space20" paddingLeft="space40">
                                                                {mg.chats.map((chat: RecentChatItem) => (
                                                                <ChatCard
                                                                    key={chat.conversationSid}
                                                                    chat={chat}
                                                                    onOpen={() => dispatch(openChatHistory(chat.conversationSid))}
                                                                />
                                                            ))}
                                                        </Box>
                                                    )}
                                                </Box>
                                            );
                                        } else {
                                            // 按天分组
                                            const monthKey = `${y}-${mg.month}`;
                                            const isMOpen = monthOpen[monthKey] === true;
                                            return (
                                                <Box key={mg.month} marginBottom="space10">
                                                    <GroupHeader
                                                        label={MONTH_NAMES[mg.month]}
                                                        count={mg.days.reduce((s, d) => s + d.chats.length, 0)}
                                                        open={isMOpen}
                                                        onToggle={() => toggleMonth(monthKey)}
                                                    />
                                                    {isMOpen && mg.days.map((dg) => {
                                                        const dayKey = `${monthKey}-${dg.date}`;
                                                        const isDOpen = monthOpen[dayKey] === true;
                                                        const dayLabel = dg.date.slice(5); // "MM-DD"
                                                        return (
                                                            <Box key={dg.date} marginBottom="space10">
                                                                <GroupHeader
                                                                    label={dayLabel}
                                                                    count={dg.chats.length}
                                                                    open={isDOpen}
                                                                    onToggle={() => toggleMonth(dayKey)}
                                                                />
                                                                {isDOpen && (
                                                                    <Box display="flex" flexDirection="column" rowGap="space20" paddingLeft="space40">
                                                                        {dg.chats.map((chat) => (
                                                                            <ChatCard
                                                                                key={chat.conversationSid}
                                                                                chat={chat}
                                                                                onOpen={() => dispatch(openChatHistory(chat.conversationSid))}
                                                                            />
                                                                        ))}
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            );
                                        }
                                    })
                                    ) : (
                                        <Box display="flex" flexDirection="column" rowGap="space20">
                                            {(yg.chats ?? []).map((chat) => (
                                                <ChatCard
                                                    key={chat.conversationSid}
                                                    chat={chat}
                                                    onOpen={() => dispatch(openChatHistory(chat.conversationSid))}
                                                />
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
        );
    };

    // ---- 渲染普通列表（未启用分组时）----
    const renderPlainList = () => {
        if (filteredChats.length === 0) {
            return <Text as="p" color="colorTextWeak">暂无历史会话。</Text>;
        }
        return (
            <Box maxHeight="400px" overflowY="auto">
                {filteredChats.map((chat) => (
                    <ChatCard
                        key={chat.conversationSid}
                        chat={chat}
                        onOpen={() => dispatch(openChatHistory(chat.conversationSid))}
                    />
                ))}
            </Box>
        );
    };

    // ---------- 渲染非分组消息 ----------
    const renderPlainMessages = () => {
        if (filteredMessages.length === 0) {
            return <Text as="p" color="colorTextWeak">暂无历史消息。</Text>;
        }
        return (
            <Box ref={scrollRef} maxHeight="400px" overflowY="auto">
                <Box textAlign="center" marginY="space30">
                    <Text as="span" fontSize="fontSize20" color="colorTextWeak">
                        共 {filteredMessages.length} 条
                    </Text>
                </Box>
                {filteredMessages.map((msg) => (
                    <HistoryMessageBubble key={msg.sid} message={msg} customerEmail={email} />
                ))}
            </Box>
        );
    };

    // ---------- 渲染分组消息 ----------
    const renderGroupedMessages = () => {
        if (filteredMessages.length === 0) {
            return <Text as="p" color="colorTextWeak">暂无历史消息。</Text>;
        }

        let msgGroups: MsgYearGroup[];
        if (range === "day") msgGroups = buildMsgGroupByDay(filteredMessages);
        else if (range === "month") msgGroups = buildMsgGroupByMonth(filteredMessages);
        else msgGroups = buildMsgGroupByYear(filteredMessages);

        if (msgGroups.length === 0) {
            return <Text as="p" color="colorTextWeak">暂无历史消息。</Text>;
        }

        return (
            <Box maxHeight="400px" overflowY="auto">
                {msgGroups.map((yg) => {
                    const y = yg.year;
                    const isYOpen = yearOpen[y] === true;
                    return (
                        <Box key={y} marginBottom="space20">
                            {/* 年份 */}
                            <GroupHeader
                                label={`${y} 年`}
                                count={
                                    yg.months
                                        ? yg.months.reduce(
                                              (s, mg) =>
                                                  s +
                                                  ("chats" in mg
                                                      ? mg.chats.length
                                                      : mg.days.reduce((ds, d) => ds + d.chats.length, 0)),
                                              0
                                          )
                                        : (yg.chats?.length ?? 0)
                                }
                                open={isYOpen}
                                onToggle={() => toggleYear(y)}
                            />
                            {isYOpen && (
                                <Box paddingLeft="space40">
                                    {yg.months ? (
                                        yg.months.map((mg) => {
                                            const monthKey = `${y}-${mg.month}`;
                                            const isMOpen = monthOpen[monthKey] === true;
                                            if ("chats" in mg) {
                                                // 按月（无天分组）
                                                return (
                                                    <Box key={mg.month} marginBottom="space10">
                                                        <GroupHeader
                                                            label={MONTH_NAMES[mg.month]}
                                                            count={mg.chats.length}
                                                            open={isMOpen}
                                                            onToggle={() => toggleMonth(monthKey)}
                                                        />
                                                        {isMOpen && (
                                                            <Box display="flex" flexDirection="column" rowGap="space20" paddingLeft="space40">
                                                                {mg.chats.map((msg) => (
                                                                    <MsgCard key={msg.sid} msg={msg} customerEmail={email} />
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                );
                                            } else {
                                                // 按天
                                                return (
                                                    <Box key={mg.month} marginBottom="space10">
                                                        <GroupHeader
                                                            label={MONTH_NAMES[mg.month]}
                                                            count={mg.days.reduce((s, d) => s + d.chats.length, 0)}
                                                            open={isMOpen}
                                                            onToggle={() => toggleMonth(monthKey)}
                                                        />
                                                        {isMOpen && mg.days.map((dg) => {
                                                            const dayKey = `${monthKey}-${dg.date}`;
                                                            const isDOpen = dayOpen[dayKey] === true;
                                                            const dayLabel = DAY_MONTH_NAMES[parseInt(dg.date.slice(8, 10), 10) - 1] || dg.date.slice(5);
                                                            return (
                                                                <Box key={dg.date} marginBottom="space10">
                                                                    <GroupHeader
                                                                        label={dayLabel}
                                                                        count={dg.chats.length}
                                                                        open={isDOpen}
                                                                        onToggle={() => toggleDay(dayKey)}
                                                                    />
                                                                    {isDOpen && (
                                                                        <Box display="flex" flexDirection="column" rowGap="space20" paddingLeft="space40">
                                                                            {dg.chats.map((msg) => (
                                                                                <MsgCard key={msg.sid} msg={msg} customerEmail={email} />
                                                                            ))}
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            );
                                                        })}
                                                    </Box>
                                                );
                                            }
                                        })
                                    ) : (
                                        <Box display="flex" flexDirection="column" rowGap="space20">
                                            {(yg.chats ?? []).map((msg) => (
                                                <MsgCard key={msg.sid} msg={msg} customerEmail={email} />
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
        );
    };

    const isGrouping = range === "year" || range === "month" || range === "day";

    return (
        <Box
            marginX="space50"
            marginTop="space40"
            marginBottom="space40"
            padding="space50"
            backgroundColor="colorBackground"
            borderRadius="borderRadius30"
            borderStyle="solid"
            borderWidth="borderWidth10"
            borderColor="colorBorderWeak"
        >
            {/* 标题栏 */}
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                marginBottom={expanded ? "space50" : "space0"}
            >
                <Text as="h3" fontSize="fontSize40" fontWeight="fontWeightSemibold">
                    聊天历史
                </Text>
                {expanded ? (
                    <Box display="flex" alignItems="center" columnGap="space30">
                        <SegmentedControl
                            value={viewMode}
                            onChange={(v) => setViewMode(v as ViewMode)}
                            options={[
                                { label: "按会话", value: "list" },
                                { label: "按消息", value: "messages" }
                            ]}
                        />
                        <Box
                            as="button"
                            type="button"
                            onClick={() => setExpanded(false)}
                            background="none"
                            border="none"
                            cursor="pointer"
                            padding="space10"
                            display="flex"
                            alignItems="center"
                        >
                            <Text as="span" fontSize="fontSize20" color="colorTextWeak">收起</Text>
                            <Text as="span" fontSize="fontSize20" color="colorTextWeak" marginLeft="space10">▲</Text>
                        </Box>
                    </Box>
                ) : (
                    <Box
                        as="button"
                        type="button"
                        onClick={() => setExpanded(true)}
                        background="none"
                        border="none"
                        cursor="pointer"
                        padding="space10"
                        display="flex"
                        alignItems="center"
                    >
                        <Text as="span" fontSize="fontSize20" color="colorTextWeak">展开</Text>
                        <Text as="span" fontSize="fontSize20" color="colorTextWeak" marginLeft="space10">▼</Text>
                    </Box>
                )}
            </Box>

            {/* 展开内容 */}
            {expanded && (
                <>
                    {/* 筛选行 */}
                    <Box
                        display="flex"
                        alignItems="center"
                        flexWrap="wrap"
                        columnGap="space20"
                        rowGap="space20"
                        paddingY="space30"
                        paddingX="space30"
                        backgroundColor={FILTER_BG}
                        borderRadius="borderRadius20"
                        borderStyle="solid"
                        borderWidth="borderWidth10"
                        borderColor="colorBorderWeak"
                        marginBottom="space40"
                    >
                        {/* 时间维度标签 */}
                        <Text as="span" fontSize="fontSize20" color="colorTextWeak" marginRight="space10">
                            时间
                        </Text>

                        {(["day", "month", "year"] as const).map((opt) => {
                            const labels = {
                                day: "按天",
                                month: "按月",
                                year: "按年"
                            } as const;
                            const active = !showCustom && range === opt;
                            return (
                                <FilterChip
                                    key={opt}
                                    label={labels[opt]}
                                    active={active}
                                    onClick={() => selectRange(opt)}
                                />
                            );
                        })}

                        {/* 分隔线 */}
                        <Box as="span" width="1px" height="16px" backgroundColor="colorBackgroundBody" />

                        {/* 自定义区间 */}
                        <FilterChip
                            label="自定义"
                            active={showCustom}
                            onClick={activateCustom}
                        />

                        {/* 自定义日期输入 */}
                        {showCustom && (
                            <Box display="flex" alignItems="center" columnGap="space10">
                                <input
                                    type="date"
                                    value={customStart}
                                    onChange={(e) => setCustomStart(e.target.value)}
                                    style={{
                                        fontSize: "12px",
                                        padding: "4px 8px",
                                        border: `1px solid ${DATE_BORDER}`,
                                        borderRadius: "6px",
                                        outline: "none",
                                        color: "#334155",
                                        background: "#fff",
                                        width: "118px"
                                    }}
                                />
                                <Text as="span" fontSize="fontSize20" color="colorTextWeak">~</Text>
                                <input
                                    type="date"
                                    value={customEnd}
                                    onChange={(e) => setCustomEnd(e.target.value)}
                                    style={{
                                        fontSize: "12px",
                                        padding: "4px 8px",
                                        border: `1px solid ${DATE_BORDER}`,
                                        borderRadius: "6px",
                                        outline: "none",
                                        color: "#334155",
                                        background: "#fff",
                                        width: "118px"
                                    }}
                                />
                            </Box>
                        )}
                    </Box>

                    {/* 加载状态 */}
                    {loading || messagesLoading ? (
                        <Box display="flex" alignItems="center" columnGap="space30" minHeight="size20">
                            <Spinner decorative size="sizeIcon20" title="加载中" />
                            <Text as="span" color="colorTextWeak" fontSize="fontSize20">
                                {viewMode === "list" ? "加载会话列表…" : "加载历史消息…"}
                            </Text>
                        </Box>
                    ) : null}

                    {error ? (
                        <Text as="p" color="colorTextError" marginBottom="space30">{error}</Text>
                    ) : null}

                    {/* === 会话列表 === */}
                    {!loading && viewMode === "list" && (
                        isGrouping ? renderGroupedChats() : renderPlainList()
                    )}

                    {/* === 消息列表 === */}
                    {!messagesLoading && viewMode === "messages" && (
                        isGrouping ? renderGroupedMessages() : (renderPlainMessages())
                    )}
                </>
            )}
        </Box>
    );
};
